import { Button } from '@bennows/boows-react';
import { Mic, MicOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { VoiceState } from './useSpeechInput';

interface Props {
  readonly state: VoiceState;
  readonly noticeVisible: boolean;
  readonly onToggle: () => void;
}

/** The mic toggle (spec 110 section 4.15.6): pressed while listening. */
export function VoiceButton({ state, onToggle }: Pick<Props, 'state' | 'onToggle'>) {
  const { t } = useTranslation();
  const listening = state === 'listening';
  return (
    <Button variant="secondary-outline" iconOnly aria-label={t(listening ? 'assistant.voice.stop' : 'assistant.voice.start')} aria-pressed={listening} onClick={onToggle}>
      {listening ? <MicOff aria-hidden="true" /> : <Mic aria-hidden="true" />}
    </Button>
  );
}

/** One polite status region for the voice states, always in the document so changes are announced. */
export function VoiceStatus({ state, noticeVisible }: Pick<Props, 'state' | 'noticeVisible'>) {
  const { t } = useTranslation();
  const lines = [
    state === 'listening' ? t('assistant.voice.listening') : null,
    noticeVisible ? t('assistant.voice.notice') : null,
    state === 'denied' ? t('assistant.voice.denied') : null,
    state === 'failed' ? t('assistant.voice.failed') : null,
    state === 'empty' ? t('assistant.voice.empty') : null,
  ].filter((line): line is string => line !== null);
  return (
    <div role="status" className="ui-assistant__voice">
      {lines.map((line) => (
        <p key={line} className="ui-assistant__note">
          {line}
        </p>
      ))}
    </div>
  );
}
